import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext } from 'react-hook-form';

import { Button } from '@/ui/Button';
import { LinearProgressBar } from '@/ui/ProgressBar';
import { ProgressBarStyled } from '@/ui/ProgressBar/variants/LinearProgressBar/styles';
import { DocumentVideoUploadButton } from '@/component/FileUpload/DocumentVideoUploadButton';
import { useFileUploader } from '@/component/FileUpload/useFileUpload';
import { useVideoUploadProgress } from '@/component/customEditorV2/ui/elements/Video/hooks/useVideoUploadProgress';
import { UploadedFileVideo } from '@/component/FileUpload/types';
import { RecordingVideoLinkModal } from '@/administration/pages/Journey/common/modals/LiveEventModal/LiveEventForm/fields/RecordingVideoLinkModal';
import { FormRecordingVideoPreview } from '@/administration/pages/Journey/common/modals/LiveEventModal/LiveEventForm/fields/FormRecording/FormRecordingVideoPreview';
import { useUpdateLocationRecordingUrlMutationRemote } from '@/store/v2';

const RecordingContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  gap: 16px;
`;

const TitleCaptionContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleStyled = styled.span`
  ${({ theme }) => theme.typography.overline};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const CaptionStyled = styled.span`
  ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const CaptionVariantStyled = styled.span`
  ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.colors.neutral.outline2};
`;

const ActionsContainerStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 100%;
  height: 36px;
  gap: 16px;
`;

const ActionTextStyled = styled.span<{ hasEventEnded: boolean }>`
  ${({ theme }) => theme.typography.overline};
  color: ${({ theme, hasEventEnded }) => (hasEventEnded ? theme.colors.neutral.onBackground : theme.colors.neutral.disabled)};
`;

const VideoLoadingStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 16px 16px 16px;
  gap: 10px;
`;

const VideoInnerStyled = styled.div`
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.neutral.background};
`;

const LoaderContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 424px;
  gap: 16px;
`;

const WrapperStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  gap: 16px;
`;

const PercentageTitleStyled = styled.span`
  ${({ theme }) => theme.typography.title5};
  letter-spacing: 0px;
  vertical-align: middle;
  color: var(--color-mine-shaft);
`;

const LinearProgressBarStyled = styled(LinearProgressBar)`
  width: 100%;

  & ${ProgressBarStyled} {
    height: 4px;

    &:after {
      height: 4px;
    }
  }
`;

const WarningStyled = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const InfoBarStyled = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 100%;
  gap: 4px;
  align-items: center;
`;

const IconStyled = styled(FontAwesomeIcon)`
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  width: 10px;
  height: 10px;
  color: ${({ theme }) => theme.colors.neutral.outline2};
`;

const VideoUploadUploaded = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  gap: 10px;
`;

const VideoUploadUploadedInner = styled.div`
  border: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const HeaderStyled = styled.div`
  display: flex;
  height: 68px;
  padding: 0 16px;
  justify-content: flex-end;
  align-items: center;
`;

const DividerStyled = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.neutral.outline1};
`;

type FormRecordingProps = {
  locationId?: number;
  eventEndTime?: string | Date;
  spaceId: number;
};

export const FormRecording = ({ locationId, eventEndTime, spaceId }: FormRecordingProps): JSX.Element => {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [uploadedVideo, setUploadedVideo] = useState<UploadedFileVideo | undefined>();
  const [videoLink, setVideoLink] = useState<string | undefined>();

  const { watch } = useFormContext<{ recordingURL?: string }>();
  const existingRecordingURL = watch('recordingURL');

  const [updateLocationRecordingUrl] = useUpdateLocationRecordingUrlMutationRemote();

  const generateVimeoUrl = (vimeoId: string): string => {
    return `https://vimeo.com/${vimeoId}`;
  };

  const saveRecordingUrl = async (url: string): Promise<void> => {
    if (!locationId) {
      return;
    }

    await updateLocationRecordingUrl({
      variables: {
        input: {
          locationId,
          recordingURL: url,
        },
      },
    });
  };

  useEffect(() => {
    if (existingRecordingURL && existingRecordingURL.trim() !== '') {
      const vimeoMatch = existingRecordingURL.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch && vimeoMatch[1]) {
        setUploadedVideo({ vimeoId: vimeoMatch[1] });
        setVideoLink(undefined);
      } else {
        setVideoLink(existingRecordingURL);
        setUploadedVideo(undefined);
      }
    } else {
      setVideoLink(undefined);
      setUploadedVideo(undefined);
    }
  }, [existingRecordingURL]);

  const videoUpload = useFileUploader<'video'>({
    spaceId,
    type: 'video',
    onUploadSuccess: async ({ vimeoId, ratio, thumbnail, duration }) => {
      const uploadedVideoData: UploadedFileVideo = {
        vimeoId,
        ratio,
        thumbnail,
        duration,
      };
      setUploadedVideo(uploadedVideoData);
      const vimeoUrl = generateVimeoUrl(vimeoId);
      await saveRecordingUrl(vimeoUrl);
    },
  });

  const transcodingInProgress = videoUpload.uploading && videoUpload.uploadingState === 'Transcoding';
  const { current: currentProgress, prev: prevProgress } = useVideoUploadProgress({
    transcodingInProgress,
    vimeoProgress: videoUpload.uploadingProgress,
    maxVisualUploadingProgress: 50,
  });

  const hasEventEnded = eventEndTime ? new Date() > new Date(eventEndTime) : false;

  const handleUploadFile = (file: File) => {
    videoUpload.uploadFile(file);
  };

  const handlePasteLink = () => {
    setIsLinkModalOpen(true);
  };

  const handleLinkSubmit = async (link: string) => {
    setVideoLink(link);
    await saveRecordingUrl(link);
  };

  const handleCloseLinkModal = () => {
    setIsLinkModalOpen(false);
  };

  const handleReplaceVideo = () => {
    setUploadedVideo(undefined);
    setVideoLink(undefined);
    // We don't clear the backend URL here since the admin might want to restore it
    // The URL will be overwritten when a new video is uploaded or pasted
  };

  if (uploadedVideo || videoLink) {
    return (
      <RecordingContainerStyled>
        <TitleCaptionContainerStyled>
          <TitleStyled>{'Recording (Optional)'}</TitleStyled>
          <CaptionStyled>Add the recorded session so learners can catch up and mark the item as completed.</CaptionStyled>
        </TitleCaptionContainerStyled>
        <VideoUploadUploaded>
          <VideoUploadUploadedInner>
            <HeaderStyled>
              <Button icon={['fas', 'repeat']} variant="ghost" onClick={handleReplaceVideo} type="button">
                Replace
              </Button>
            </HeaderStyled>
            <DividerStyled />
            <FormRecordingVideoPreview uploadedFileVideo={uploadedVideo} videoLink={videoLink} borderBottomRadius="4px" />
          </VideoUploadUploadedInner>
        </VideoUploadUploaded>
      </RecordingContainerStyled>
    );
  }

  if (videoUpload.uploading) {
    const progressText = `Uploading ${currentProgress}%`;

    return (
      <RecordingContainerStyled>
        <TitleCaptionContainerStyled>
          <TitleStyled>{'Recording (Optional)'}</TitleStyled>
          <CaptionStyled>Add the recorded session so learners can catch up and mark the item as completed.</CaptionStyled>
        </TitleCaptionContainerStyled>
        <VideoLoadingStyled>
          <VideoInnerStyled>
            <LoaderContainerStyled>
              <WrapperStyled>
                <PercentageTitleStyled>{progressText}</PercentageTitleStyled>
                <LinearProgressBarStyled
                  maxValue={100}
                  animationStartPercentage={prevProgress}
                  value={currentProgress}
                  animated={true}
                  rounded
                />
              </WrapperStyled>
              <WarningStyled>Do not leave this page until the upload is complete, or progress will be lost</WarningStyled>
            </LoaderContainerStyled>
          </VideoInnerStyled>
        </VideoLoadingStyled>
      </RecordingContainerStyled>
    );
  }

  return (
    <RecordingContainerStyled>
      <RecordingVideoLinkModal open={isLinkModalOpen} onClose={handleCloseLinkModal} onSubmit={handleLinkSubmit} />
      <TitleCaptionContainerStyled>
        <TitleStyled>{'Recording (Optional)'}</TitleStyled>
        <CaptionStyled>Add the recorded session so learners can catch up and mark the item as completed.</CaptionStyled>
      </TitleCaptionContainerStyled>

      <ActionsContainerStyled>
        <DocumentVideoUploadButton
          icon={['far', 'file-upload']}
          variant="secondary"
          size="large"
          uploadFile={handleUploadFile}
          fileType="video"
          disabled={!hasEventEnded}
          type="button"
        >
          Upload
        </DocumentVideoUploadButton>
        <ActionTextStyled hasEventEnded={hasEventEnded}>Or</ActionTextStyled>
        <Button
          icon={['far', 'link']}
          variant="secondary"
          size="large"
          onClick={handlePasteLink}
          type="button"
          disabled={!hasEventEnded}
        >
          Paste link
        </Button>
      </ActionsContainerStyled>

      {!hasEventEnded && (
        <InfoBarStyled>
          <IconStyled icon={['far', 'circle-info']} />
          <CaptionVariantStyled>Upload options will be available once the session has ended.</CaptionVariantStyled>
        </InfoBarStyled>
      )}
    </RecordingContainerStyled>
  );
};
